/**
 * Platform bootstrap — the unauthenticated call resolved before first paint
 * that supplies every origin/URL this console needs beyond the derived API host
 * (`src/config/platform.ts`).
 *
 * Fetched with plain `fetch`, NOT the HTTP client — the client needs
 * `authOrigin` to resolve base URLs, so routing bootstrap through it would be
 * circular.
 *
 * Stored in a module-scoped variable only — never localStorage, sessionStorage,
 * cookies, or a service worker cache (hard constraint). `getBootstrap()` throws
 * if called before resolution: that is a programming error the blocking
 * `BootstrapGate` makes impossible, and a throw surfaces it immediately instead
 * of silently producing `undefined` in a URL.
 */
import { apiBase } from './platform';

export interface PlatformBootstrap {
  productSlug: string;
  productName: string;
  /** Complete URL. Use verbatim; append only `?returnUrl=`. */
  loginUrl: string;
  /** Platform auth host for a console — NOT a per-tenant AUTH host. */
  authOrigin: string;
  /**
   * Payment return allowlist origin. OPTIONAL and absent for this console by
   * design — a product-admin console has no payment flow. Typed and stored;
   * read nowhere.
   */
  payOrigin?: string;
  deployEnv?: string;
}

/** Why bootstrap failed. `http` = non-2xx (status carried), `network` = the
 *  request never completed (status 0), `shape` = a 2xx whose body is not the
 *  product-admin bootstrap shape (e.g. the backend took the tenant branch). */
export type BootstrapFailureKind = 'http' | 'network' | 'shape';

/** Thrown by `fetchBootstrap`. Carries the numeric status so the gate can
 * special-case 404 (host not registered) from any other failure. */
export class BootstrapError extends Error {
  public readonly status: number;
  public readonly kind: BootstrapFailureKind;

  constructor(status: number, kind: BootstrapFailureKind = status === 0 ? 'network' : 'http') {
    super(
      kind === 'shape'
        ? 'Bootstrap response is not the product-admin shape'
        : `Bootstrap request failed with status ${status}`,
    );
    this.name = 'BootstrapError';
    this.status = status;
    this.kind = kind;
  }
}

/**
 * The product-admin branch of the backend's bootstrap: three non-empty string
 * fields and NO tenant identity. A `tenantSlug` means the backend served the
 * tenant branch for this host — treated as a failed bootstrap, never adopted.
 */
export function isProductAdminBootstrap(value: unknown): value is PlatformBootstrap {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  if ('tenantSlug' in v) return false;
  return (
    typeof v.productSlug === 'string' &&
    v.productSlug.length > 0 &&
    typeof v.loginUrl === 'string' &&
    v.loginUrl.length > 0 &&
    typeof v.authOrigin === 'string' &&
    v.authOrigin.length > 0
  );
}

export async function fetchBootstrap(): Promise<PlatformBootstrap> {
  let res: Response;
  try {
    res = await fetch(`${apiBase()}/bootstrap`);
  } catch {
    throw new BootstrapError(0);
  }
  if (!res.ok) {
    throw new BootstrapError(res.status);
  }
  const body = (await res.json()) as unknown;
  if (!isProductAdminBootstrap(body)) {
    throw new BootstrapError(res.status, 'shape');
  }
  return body;
}

let bootstrap: PlatformBootstrap | null = null;

export function setBootstrap(value: PlatformBootstrap): void {
  bootstrap = value;
}

export function getBootstrap(): PlatformBootstrap {
  if (!bootstrap) {
    throw new Error('getBootstrap() called before bootstrap resolved');
  }
  return bootstrap;
}

/** Test seam. */
export function __resetBootstrap(): void {
  bootstrap = null;
}
